import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {
  Typography,
  Card,
  CardContent,
  Grid,
  CardActionArea,
  Box,
  Modal,
  IconButton,
} from '@mui/material';
import { includes } from 'lodash';
import React, { useRef, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { irregularitiesSlice, useDispatch, useSelector } from '../../stores';
import { palette } from '../../theme';
import { handleDateTime } from '../../utils';

export const IrregularityCard = (props: {
  disabled?: boolean;
  item: {
    brand: string;
    id: number;
    observation: string | null;
    plate: string;
    plateImg: string;
    ticketImg: string | null;
    address: string | null;
    registerDateTime: string;
    type: string;
  };
}) => {
  const dispatch = useDispatch();
  const cardRef = useRef(null);
  const [lightbox, setLightbox] = useState<{
    open: boolean;
    src: string;
    alt: string;
  }>({ open: false, src: '', alt: '' });
  const { irregularityPayablesList, irregularityUnpayablesList, selectedIds } =
    useSelector((state) => state.irregularitiesReducer);

  const { disabled = false } = props;

  const openLightbox = (e: React.MouseEvent, src: string, alt: string) => {
    e.stopPropagation();
    setLightbox({ open: true, src, alt });
  };

  const closeLightbox = () => setLightbox((prev) => ({ ...prev, open: false }));

  const selected = !disabled && includes(selectedIds, props.item.id);

  const handleClick = (irregularityId: number) => {
    if (!disabled) {
      let newSelectedIds: number[];

      if (!selected) {
        newSelectedIds = [...selectedIds, irregularityId];
      } else {
        newSelectedIds = selectedIds.filter((id) => id !== irregularityId);
      }

      dispatch(irregularitiesSlice.actions.setSelectedIds(newSelectedIds));

      const allSelected = irregularityPayablesList.every((item) =>
        newSelectedIds.includes(item.id),
      );
      dispatch(irregularitiesSlice.actions.setAllIdsSelected(allSelected));
    }
  };

  return (
    <Grid
      item
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      {!disabled && selected && (
        <div
          style={{
            position: 'absolute',
            top: '-6px',
            right: '-10px',
            height: '25px',
            width: '25px',
            backgroundColor: palette.secondary.main,
            borderRadius: '100px',
            zIndex: 1,
          }}
        >
          <CheckIcon sx={{ fontSize: '25px', color: 'white' }} />
        </div>
      )}
      <Card
        sx={{
          width: '100%',
          borderRadius: '14px',
          backgroundColor: disabled ? 'rgba(0, 0, 0, 0.08)' : 'white',
          border: disabled
            ? 'default'
            : selected
              ? `3px solid ${palette.secondary.main}`
              : `3px solid ${palette.primary.main}`,
        }}
        ref={cardRef}
        onClick={() => handleClick(props.item.id)}
      >
        <CardActionArea
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
            height: { xs: 170, sm: 150 },
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              padding: '12px',
              width: {
                xs: '100%',
                sm: '50%',
              },
              flex: '1',
            }}
          >
            <Typography
              variant='subtitle1'
              color='primary'
              sx={{ fontWeight: 700, lineHeight: 1.15 }}
            >
              {props.item.id}
            </Typography>
            <Typography
              gutterBottom
              color='text.secondary'
              sx={{
                fontSize: '12px',
                lineHeight: 1.15,
              }}
            >
              {handleDateTime(props.item.registerDateTime)}
            </Typography>
            <Typography
              color='text.secondary'
              sx={{
                fontSize: '13px',
                lineHeight: 1.25,
              }}
            >
              <strong>Placa:</strong> {props.item.plate}
            </Typography>
            <Typography
              color='text.secondary'
              sx={{
                fontSize: '13px',
                lineHeight: 1.25,
              }}
            >
              <strong>Categoria:</strong> {props.item.type}
            </Typography>
            <Typography
              color='text.secondary'
              sx={{
                fontSize: '13px',
                lineHeight: 1.25,
              }}
            >
              <strong>Modelo:</strong> {props.item.brand}
            </Typography>
            {props.item.address && (
              <Typography
                color='text.secondary'
                sx={{
                  fontSize: '13px',
                  lineHeight: 1.25,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                <strong>Endereço:</strong> {props.item.address}
              </Typography>
            )}
          </CardContent>
          <Box
            sx={{
              width: {
                xs: '100%',
                sm: '50%',
              },
              // minHeight: { xs: 120, sm: 140 },
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
              gap: '6px',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            {/* Plate Image */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                minWidth: 0,
                minHeight: 0,
                overflow: 'hidden',
                cursor: props.item.plateImg ? 'pointer' : 'default',
              }}
              onClick={
                props.item.plateImg
                  ? (e) => openLightbox(e, props.item.plateImg, 'Placa')
                  : undefined
              }
            >
              {props.item.plateImg ? (
                <>
                  <img
                    alt='placa'
                    src={props.item.plateImg}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      width: 30,
                      height: 30,
                      borderRadius: '999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.45)',
                      color: 'white',
                      opacity: 0.9,
                      transition: 'opacity 120ms ease, transform 120ms ease',
                      pointerEvents: 'none',
                      transform: 'scale(1)',
                      '.MuiCardActionArea-root:hover &': {
                        opacity: 1,
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    <ZoomInIcon sx={{ fontSize: 18 }} />
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    color='text.secondary'
                    sx={{
                      fontSize: '12px',
                      textAlign: 'center',
                    }}
                  >
                    Sem imagem da placa
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Ticket Image */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                minWidth: 0,
                minHeight: 0,
                overflow: 'hidden',
                cursor: props.item.ticketImg ? 'pointer' : 'default',
              }}
              onClick={
                props.item.ticketImg
                  ? (e) =>
                      openLightbox(e, props.item.ticketImg as string, 'Ticket')
                  : undefined
              }
            >
              {props.item.ticketImg ? (
                <>
                  <img
                    alt='multa'
                    src={props.item.ticketImg}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      width: 30,
                      height: 30,
                      borderRadius: '999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.45)',
                      color: 'white',
                      opacity: 0.9,
                      transition: 'opacity 120ms ease, transform 120ms ease',
                      pointerEvents: 'none',
                      transform: 'scale(1)',
                      '.MuiCardActionArea-root:hover &': {
                        opacity: 1,
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    <ZoomInIcon sx={{ fontSize: 18 }} />
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    color='text.secondary'
                    sx={{
                      fontSize: '12px',
                      textAlign: 'center',
                    }}
                  >
                    Sem imagem do ticket
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </CardActionArea>
      </Card>

      {/* Lightbox: full-size image overlay */}
      <Modal
        open={lightbox.open}
        onClose={closeLightbox}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            outline: 'none',
            position: 'relative',
            width: '90vw',
            height: '90vh',
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 24,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            onClick={closeLightbox}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              },
            }}
            size='small'
          >
            <CloseIcon />
          </IconButton>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>
      </Modal>
    </Grid>
  );
};
